import React from 'react';
import { Search } from 'lucide-react';
import { PriceRangeSlider } from './PriceRangeSlider';
import { WeightRangeSlider } from './WeightRangeSlider';

interface FiltersState {
  productClass: string[];
  categories: string[];
  brands: string[];
  tags: string[];
  colors: string[];
  priceRange: [number, number];
  weightRange: [number, number];
  stockStatus: {
    inStock: boolean;
    outOfStock: boolean;
  };
  discountStatus: {
    hasDiscount: boolean;
    noDiscount: boolean;
  };
}

interface SortOption {
  value: 'price-asc' | 'price-desc' | 'discount' | 'new';
  label: string;
}

interface ProductFiltersProps {
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  activeFilters: FiltersState;
  setActiveFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
  sortBy: string;
  setSortBy: (sort: any) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  uniqueBrands: string[];
  uniqueTags: string[];
  uniqueColors: string[];
  productClasses: { id: string; name: string }[];
  categories: string[];
  resetAllFilters: () => void;
  colorCodes?: {[key: string]: string};
  pageType?: '/toukeratas' | '/toukeratas/lenks' | '/toukeratas/tald' | '/toukeratas/brandid'
    | '/toukeratas/rattad' | '/toukeratas/kahvlid' | '/toukeratas/klambrid'
    | '/toukeratas/lenksuotsad' | '/toukeratas/peegid' | '/toukeratas/kaepidemed'
    | '/toukeratas/gripteibid' | '/toukeratas/laste-toukeratas';
  h1Title?: string;
}

export function ProductFilters({
  showFilters,
  setShowFilters,
  activeFilters,
  setActiveFilters,
  sortBy,
  setSortBy,
  searchQuery,
  setSearchQuery,
  uniqueBrands,
  uniqueTags,
  uniqueColors,
  productClasses,
  categories,
  resetAllFilters,
  colorCodes,
  pageType = '/toukeratas',
  h1Title
}: ProductFiltersProps) {
  const getTitleByPageType = () => {
    // Use h1Title if provided (from seoData)
    if (h1Title) return h1Title;

    switch (pageType) {
      case '/toukeratas/lenks':
        return 'Tõukeratta lenks';
      case '/toukeratas/tald':
        return 'Tõukeratta tald';
      case '/toukeratas/brandid':
        return 'Tõukeratta brändid';
      default:
        return 'Tõukerattad';
    }
  };

  return (
    <div className="flex justify-between items-center mb-8">
      <h1 className="text-3xl font-bold">{getTitleByPageType()}</h1>
    </div>
  );
}

export function ProductFilterSidebar({
  showFilters,
  activeFilters,
  setActiveFilters,
  sortBy,
  setSortBy,
  searchQuery,
  setSearchQuery,
  uniqueBrands,
  uniqueTags,
  uniqueColors,
  productClasses,
  categories,
  colorCodes = {}
}: ProductFiltersProps) {
  const sortOptions: SortOption[] = [
    { value: 'price-desc', label: 'Hind: kõrgem enne' },
    { value: 'price-asc', label: 'Hind: madalam enne' },
    { value: 'discount', label: 'Allahindlus' },
    { value: 'new', label: 'Uued tooted' }
  ];

  return (
    <div className={`
      md:w-64 bg-white rounded-lg shadow-lg p-6 h-fit select-none
      ${showFilters ? 'block' : 'hidden md:block'}
    `}>
      <h2 className="text-lg font-bold mb-6">Filtreeri tõukerattad</h2>
      <div className="space-y-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Otsi tooteid..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent select-text"
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
        </div>

        {/* Sorteeri section - commented out temporarily
        <div>
          <h3 className="font-bold mb-3">Sorteeri</h3>
          <div className="space-y-2">
            {sortOptions.map(option => (
              <label key={option.value} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={sortBy === option.value}
                  onChange={() => setSortBy(option.value)}
                  className="rounded-full border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
                />
                <span className="ml-2">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
        */}

        <div>
          <h3 className="font-bold mb-3">Toote tüüp</h3>
          <div className="space-y-2">
            {productClasses.map(productClass => (
              <label key={productClass.id} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeFilters.productClass.includes(productClass.id)}
                  onChange={() => {
                    setActiveFilters(prev => ({
                      ...prev,
                      productClass: prev.productClass.includes(productClass.id)
                        ? prev.productClass.filter(id => id !== productClass.id)
                        : [...prev.productClass, productClass.id]
                    }));
                  }}
                  className="rounded border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
                />
                <span className="ml-2 font-medium text-sm">{productClass.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-bold mb-3">Kategooriad</h3>
          <div className="space-y-2">
            {categories.map(category => (
              <label key={category} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeFilters.categories.includes(category)}
                  onChange={() => {
                    setActiveFilters(prev => ({
                      ...prev,
                      categories: prev.categories.includes(category)
                        ? prev.categories.filter(c => c !== category)
                        : [...prev.categories, category]
                    }));
                  }}
                  className="rounded border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
                />
                <span className="ml-2 font-medium text-sm">{category}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-bold mb-3">Sildid</h3>
          <div className="space-y-2">
            {uniqueTags.map(tag => (
              <label key={tag} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeFilters.tags.includes(tag)}
                  onChange={() => {
                    setActiveFilters(prev => ({
                      ...prev,
                      tags: prev.tags.includes(tag)
                        ? prev.tags.filter(t => t !== tag)
                        : [...prev.tags, tag]
                    }));
                  }}
                  className="rounded border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
                />
                <span className="ml-2 font-medium text-sm">{tag}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-bold mb-3">Brändid</h3>
          <div className="space-y-2">
            {uniqueBrands.map(brand => (
              <label key={brand} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeFilters.brands.includes(brand.toLowerCase())}
                  onChange={() => {
                    setActiveFilters(prev => ({
                      ...prev,
                      brands: prev.brands.includes(brand.toLowerCase())
                        ? prev.brands.filter(b => b !== brand.toLowerCase())
                        : [...prev.brands, brand.toLowerCase()]
                    }));
                  }}
                  className="rounded border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
                />
                <span className="ml-2 font-medium text-sm">{brand}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-bold mb-3">Värvid</h3>
          <div className="flex flex-wrap gap-2">
            {uniqueColors.map(color => {
              // Get color code from map or fallback to the color name
              const colorCode = colorCodes[color] || color;
              return (
                <button
                  key={color}
                  onClick={() => {
                    setActiveFilters(prev => ({
                      ...prev,
                      colors: prev.colors.includes(color)
                        ? prev.colors.filter(c => c !== color)
                        : [...prev.colors, color]
                    }));
                  }}
                  className={`w-6 h-6 rounded-full border-2 transition-all ${
                    activeFilters.colors.includes(color)
                      ? 'border-[#4FC1E9] scale-110'
                      : 'border-transparent hover:border-gray-200'
                  }`}
                  style={{ 
                    backgroundColor: colorCode || '#CCCCCC',
                    boxShadow: !colorCode ? 'inset 0 0 0 1px rgba(0,0,0,0.1)' : 'none'
                  }}
                  title={color}
                />
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="font-bold mb-3">Hind</h3>
          <PriceRangeSlider
            min={0}
            max={1000}
            value={activeFilters.priceRange}
            onChange={(value) => {
              setActiveFilters(prev => ({
                ...prev,
                priceRange: value
              }));
            }}
          />
        </div>

        <div>
          <h3 className="font-bold mb-3">Kaal</h3>
          <WeightRangeSlider
            min={0}
            max={5}
            value={activeFilters.weightRange}
            onChange={(value) => {
              setActiveFilters(prev => ({
                ...prev,
                weightRange: value
              }));
            }}
          />
        </div>

        <div>
          <h3 className="font-bold mb-3">Laoseis</h3>
          <div className="space-y-2">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={activeFilters.stockStatus.inStock}
                onChange={() => {
                  setActiveFilters(prev => ({
                    ...prev,
                    stockStatus: {
                      ...prev.stockStatus,
                      inStock: !prev.stockStatus.inStock
                    }
                  }));
                }}
                className="rounded border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
              />
              <span className="ml-2 font-medium text-sm">Laos</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={activeFilters.stockStatus.outOfStock}
                onChange={() => {
                  setActiveFilters(prev => ({
                    ...prev,
                    stockStatus: {
                      ...prev.stockStatus,
                      outOfStock: !prev.stockStatus.outOfStock
                    }
                  }));
                }}
                className="rounded border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
              />
              <span className="ml-2 font-medium text-sm">Laost otsas</span>
            </label>
          </div>
        </div>

        {/* Allahindlus section - commented out temporarily
        <div>
          <h3 className="font-bold mb-3">Allahindlus</h3>
          <div className="space-y-2">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={activeFilters.discountStatus.hasDiscount}
                onChange={() => {
                  setActiveFilters(prev => ({
                    ...prev,
                    discountStatus: {
                      ...prev.discountStatus,
                      hasDiscount: !prev.discountStatus.hasDiscount
                    }
                  }));
                }}
                className="rounded border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
              />
              <span className="ml-2">Soodushinnaga</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={activeFilters.discountStatus.noDiscount}
                onChange={() => {
                  setActiveFilters(prev => ({
                    ...prev,
                    discountStatus: {
                      ...prev.discountStatus,
                      noDiscount: !prev.discountStatus.noDiscount
                    }
                  }));
                }}
                className="rounded border-gray-300 text-[#4FC1E9] focus:ring-[#4FC1E9]"
              />
              <span className="ml-2">Täishinnaga</span>
            </label>
          </div>
        </div>
        */}
      </div>
    </div>
  );
}

export function NoProductsFound({ resetAllFilters }: { resetAllFilters: () => void }) {
  return (
    <div className="text-center py-12">
      <p className="text-gray-500 mb-4">Ühtegi toodet ei leitud.</p>
      <button 
        onClick={resetAllFilters}
        className="text-[#4FC1E9] hover:text-[#3ba8d0]"
      >
        Tühista kõik filtrid
      </button>
    </div>
  );
}
